import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '@/hooks/use-debounce';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { Calendar } from '@/components/ui-kit/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui-kit/popover';
import { Separator } from '@/components/ui-kit/separator';
// import { useProjectStore } from '@/modules/identifier/state/use-project-store';
import {
  MessageSquareShare,
  CalendarDays,
  Mail,
  Search,
  User,
  Loader2,
  Check,
  ChevronsUpDown,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import useIsMobile from '@/hooks/use-is-mobile';
import { cn, formatDate } from '@/lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui-kit/command';

import {
  AgentConversationListFilterProps,
  ExpandedFilter,
  PlaygroundFilter,
} from '../utils/filter-types';
import { useUserSearch } from '../hooks/use-user-search';
import { useDateFilter } from '../hooks/use-date-filter';

export const AgentConversationListFilter = ({
  activeTab,
  queryParams,
  setQueryParams,
  playgroundFilter,
  onPlaygroundFilterChange,
}: AgentConversationListFilterProps) => {
  const { t } = useTranslation();
  // const projectKey = useProjectStore().selectedProject?.tenantId || '';
  const isMobile = useIsMobile();
  const currentFilterType = queryParams.filter_type || 'name';

  const {
    userSearchTerm,
    allUsers,
    isUsersLoading,
    isUsersFetching,
    isUserPopoverOpen,
    setIsUserPopoverOpen,
    commandListRef,
    handleUserScroll,
    handleUserWheel,
    handleUserSearchChange,
    handleFilterTypeChange,
    handleUserSelection,
    handleEmailSelection,
    getSelectedLabel,
  } = useUserSearch({ currentFilterType, queryParams, setQueryParams });

  const {
    dateOpen,
    setDateOpen,
    dateValue,
    localDate,
    setLocalDate,
    hasDateSelected,
    handleDateSelect,
    handleDateReset,
    handleDateApply,
  } = useDateFilter({ queryParams, setQueryParams });

  const [expandedFilter, setExpandedFilter] = useState<ExpandedFilter>(null);
  const [localQuery, setLocalQuery] = useState(queryParams.query || '');
  const debouncedQuery = useDebounce(localQuery, 500);

  useEffect(() => {
    setQueryParams((prev: any) => {
      if (prev.query === debouncedQuery) return prev;
      return { ...prev, query: debouncedQuery, page: 1 };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  useEffect(() => {
    if (!queryParams.query && localQuery) {
      setLocalQuery('');
    }
  }, [queryParams.query]);

  useEffect(() => {
    setExpandedFilter(null);
  }, [activeTab]);

  const toggleFilter = (filter: ExpandedFilter) => {
    setExpandedFilter((prev) => (prev === filter ? null : filter));
  };

  const isSearchActive = !!queryParams.query;
  const isDropdownActive =
    activeTab === 'private'
      ? !!(queryParams.user_id || queryParams.user_email)
      : playgroundFilter !== 'all';

  const renderIconButton = (
    filter: ExpandedFilter,
    icon: React.ReactNode,
    title: string,
    hasActive?: boolean,
    spread?: boolean
  ) => (
    <Button
      size="icon"
      variant={hasActive ? 'default' : 'ghost'}
      onClick={() => toggleFilter(filter)}
      className={`h-9 shrink-0 transition-all duration-200 ${spread ? 'flex-1' : 'w-9'} ${
        hasActive ? 'bg-blue-600 text-white hover:bg-blue-700' : ''
      }`}
      title={title}
    >
      {icon}
    </Button>
  );

  const renderSearchExpanded = () => (
    <div className="flex flex-1 items-center gap-1 transition-all duration-200">
      <div className="relative flex-1">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input
          placeholder={t('SEARCH_BY_TITLES_PLACEHOLDER')}
          className="h-9 pl-9 text-sm"
          value={localQuery}
          onChange={(e) => setLocalQuery(e.target.value)}
          autoFocus
        />
      </div>
    </div>
  );

  const renderDropdownExpandedPrivate = () => {
    const selectedValue =
      currentFilterType === 'name' ? queryParams.user_id : queryParams.user_email;

    return (
      <div className="flex flex-1 items-center transition-all duration-200">
        <Select
          value={currentFilterType}
          onValueChange={(val) => handleFilterTypeChange(val as 'name' | 'email')}
        >
          <SelectTrigger className="h-9 w-[60px] rounded-r-none border-r-0 focus:ring-0">
            <SelectValue>
              {currentFilterType === 'email' ? (
                <Mail className="h-4 w-4" />
              ) : (
                <User className="h-4 w-4" />
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="name">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
              </div>
            </SelectItem>
            <SelectItem value="email">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
              </div>
            </SelectItem>
          </SelectContent>
        </Select>

        <Popover open={isUserPopoverOpen} onOpenChange={setIsUserPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={isUserPopoverOpen}
              className="h-9 flex-1 justify-between rounded-l-none border-l-0 focus:ring-0"
            >
              <span className="truncate text-sm">
                {selectedValue
                  ? getSelectedLabel()
                  : currentFilterType === 'name'
                    ? t('SELECT_USER')
                    : t('SELECT_EMAIL')}
              </span>
              <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[250px] p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder={
                  currentFilterType === 'name'
                    ? t('SEARCH_BY_NAME_PLACEHOLDER')
                    : t('SEARCH_BY_EMAIL_PLACEHOLDER')
                }
                value={userSearchTerm}
                onValueChange={handleUserSearchChange}
              />
              <CommandList
                ref={commandListRef}
                onScroll={handleUserScroll}
                onWheel={handleUserWheel}
                className="max-h-[180px] overflow-y-auto"
                style={{ overflowY: 'auto' }}
              >
                {!isUsersLoading && !isUsersFetching && allUsers.length === 0 && (
                  <CommandEmpty>{t('NO_USERS_FOUND')}</CommandEmpty>
                )}
                {(isUsersLoading || isUsersFetching) && allUsers.length === 0 && (
                  <div className="flex items-center justify-center p-4 text-sm text-gray-500">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('LOADING')}
                  </div>
                )}
                <CommandGroup>
                  {allUsers.map((user) => {
                    const value = currentFilterType === 'name' ? user.itemId : user.email;
                    const label =
                      currentFilterType === 'name'
                        ? `${user.firstName} ${user.lastName}`
                        : user.email;

                    return (
                      <CommandItem
                        key={user.itemId}
                        value={label}
                        onSelect={() => {
                          if (currentFilterType === 'name') {
                            handleUserSelection(user.itemId);
                          } else {
                            handleEmailSelection(user.email);
                          }
                        }}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            selectedValue === value ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        <span className="truncate">{label}</span>
                      </CommandItem>
                    );
                  })}
                  {(isUsersLoading || isUsersFetching) && allUsers.length > 0 && (
                    <div className="flex items-center justify-center p-2 text-sm text-gray-500">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('LOADING_MORE')}
                    </div>
                  )}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    );
  };

  const renderDropdownExpandedPublic = () => (
    <div className="flex flex-1 items-center gap-1 transition-all duration-200">
      <Select
        value={playgroundFilter}
        onValueChange={(val) => onPlaygroundFilterChange(val as PlaygroundFilter)}
      >
        <SelectTrigger className="h-9 flex-1 focus:ring-0">
          <SelectValue placeholder={t('ALL')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('ALL')}</SelectItem>
          <SelectItem value="playground">{t('PLAYGROUND')}</SelectItem>
          <SelectItem value="non-playground">{t('NON_PLAYGROUND')}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );

  const renderDateExpanded = () => (
    <div className="flex items-center gap-1 transition-all duration-200">
      <Popover
        open={dateOpen}
        onOpenChange={(open) => {
          setDateOpen(open);
          setLocalDate(dateValue);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={`h-9 border-dashed ${hasDateSelected ? 'border-blue-500 text-blue-600 dark:text-blue-400' : ''}`}
          >
            <div className="flex items-center">
              <CalendarDays className="mr-2 h-4 w-4" />
              <span>{t('DATE')}</span>
            </div>
            {dateValue?.from && (
              <>
                <Separator orientation="vertical" className="mx-2 h-4" />
                <span className="text-xs">
                  {formatDate(dateValue.from, true)}
                  {dateValue.to && ` - ${formatDate(dateValue.to, true)}`}
                </span>
              </>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={localDate?.from}
            selected={localDate?.from ? { from: localDate.from, to: localDate.to } : undefined}
            onSelect={handleDateSelect}
            numberOfMonths={isMobile ? 1 : 2}
          />
          <div className="flex items-center gap-4 px-3 pb-4">
            <Button type="button" variant="outline" className="w-full" onClick={handleDateReset}>
              {t('RESET')}
            </Button>
            <Button type="button" className="w-full" onClick={handleDateApply}>
              {t('APPLY')}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );

  return (
    <div className="flex items-center gap-1 border-b border-gray-200 px-3 py-2 dark:border-gray-700">
      {expandedFilter === 'search' ? (
        <>
          {renderSearchExpanded()}
          {renderIconButton(
            'dropdown',
            activeTab === 'private' ? (
              <User className="h-4 w-4" />
            ) : (
              <MessageSquareShare className="h-4 w-4" />
            ),
            activeTab === 'private' ? t('FILTER_BY_USER') : t('FILTER_BY_TYPE'),
            isDropdownActive
          )}
          {renderIconButton(
            'date',
            <CalendarDays className="h-4 w-4" />,
            t('FILTER_BY_DATE'),
            hasDateSelected
          )}
        </>
      ) : expandedFilter === 'dropdown' ? (
        <>
          {renderIconButton('search', <Search className="h-4 w-4" />, t('SEARCH'), isSearchActive)}
          {activeTab === 'private'
            ? renderDropdownExpandedPrivate()
            : renderDropdownExpandedPublic()}
          {renderIconButton(
            'date',
            <CalendarDays className="h-4 w-4" />,
            t('FILTER_BY_DATE'),
            hasDateSelected
          )}
        </>
      ) : expandedFilter === 'date' ? (
        <>
          {renderIconButton('search', <Search className="h-4 w-4" />, t('SEARCH'), isSearchActive)}
          {renderIconButton(
            'dropdown',
            activeTab === 'private' ? (
              <User className="h-4 w-4" />
            ) : (
              <MessageSquareShare className="h-4 w-4" />
            ),
            activeTab === 'private' ? t('FILTER_BY_USER') : t('FILTER_BY_TYPE'),
            isDropdownActive
          )}
          {renderDateExpanded()}
        </>
      ) : (
        <>
          {renderIconButton(
            'search',
            <Search className="h-4 w-4" />,
            t('SEARCH'),
            isSearchActive,
            true
          )}
          {renderIconButton(
            'dropdown',
            activeTab === 'private' ? (
              <User className="h-4 w-4" />
            ) : (
              <MessageSquareShare className="h-4 w-4" />
            ),
            activeTab === 'private' ? t('FILTER_BY_USER') : t('FILTER_BY_TYPE'),
            isDropdownActive,
            true
          )}
          {renderIconButton(
            'date',
            <CalendarDays className="h-4 w-4" />,
            t('FILTER_BY_DATE'),
            hasDateSelected,
            true
          )}
        </>
      )}
    </div>
  );
};
